import React, { useState, useEffect } from "react";
import styled from "styled-components";

interface Step2DateSelectionProps {
  MonthRadioBoxGroup: React.ComponentType<any>;
  WeekRadioGroupFirst: React.ComponentType<any>;
  WeekRadioGroupSecond: React.ComponentType<any>;
  DayRadioGroupFirst: React.ComponentType<any>;
  DayRadioGroupSecond: React.ComponentType<any>;
  TimePopupSelectFirst: React.ComponentType<any>;
  TimePopupSelectSecond: React.ComponentType<any>;
  propsToInjectFirst: any;
  propsToInjectSecond: any;
  times: any[];
  selectedMonthIds?: number[];
  selectedWeekIdsFirst?: number[];
  selectedWeekIdsSecond?: number[];
  selectedDayIdsFirst?: number[];
  selectedDayIdsSecond?: number[];
  activeTimeIdsFirst?: number[];
  activeTimeIdsSecond?: number[];
  selectedDayFirst?: string;
  selectedDaySecond?: string;
  selectedTimeFirst?: string;
  selectedTimeSecond?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  onDaySelectFirst?: (dayId: number) => void;
  onDaySelectSecond?: (dayId: number) => void;
  onTimeSelectFirst?: (timeName: string) => void;
  onTimeSelectSecond?: (timeName: string) => void;
}

export default function Step2DateSelection({
  MonthRadioBoxGroup,
  WeekRadioGroupFirst,
  WeekRadioGroupSecond,
  DayRadioGroupFirst: _DayRadioGroupFirst,
  DayRadioGroupSecond: _DayRadioGroupSecond,
  TimePopupSelectFirst: _TimePopupSelectFirst,
  TimePopupSelectSecond: _TimePopupSelectSecond,
  propsToInjectFirst: _propsToInjectFirst,
  propsToInjectSecond: _propsToInjectSecond,
  times,
  selectedMonthIds,
  selectedWeekIdsFirst,
  selectedWeekIdsSecond,
  selectedDayIdsFirst: _selectedDayIdsFirst,
  selectedDayIdsSecond: _selectedDayIdsSecond,
  selectedDayFirst,
  selectedDaySecond,
  selectedTimeFirst,
  selectedTimeSecond,
  onNext,
  onPrevious,
  onDaySelectFirst,
  onDaySelectSecond,
  onTimeSelectFirst,
  onTimeSelectSecond,
}: Step2DateSelectionProps) {
  const [showDayModal, setShowDayModal] = useState(false);
  const [showDayModalSecond, setShowDayModalSecond] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showTimeModalSecond, setShowTimeModalSecond] = useState(false);
  const [localSelectedDay, setLocalSelectedDay] = useState<string>(selectedDayFirst || "- 요일");
  const [localSelectedDaySecond, setLocalSelectedDaySecond] = useState<string>(
    selectedDaySecond || "- 요일"
  );
  const [localSelectedTime, setLocalSelectedTime] = useState<string>(selectedTimeFirst || "- 시");
  const [localSelectedTimeSecond, setLocalSelectedTimeSecond] = useState<string>(
    selectedTimeSecond || "- 시"
  );

  // props가 변경될 때 로컬 상태 동기화
  useEffect(() => {
    if (selectedDayFirst) {
      setLocalSelectedDay(selectedDayFirst);
    }
    if (selectedDaySecond) {
      setLocalSelectedDaySecond(selectedDaySecond);
    }
    if (selectedTimeFirst) {
      setLocalSelectedTime(selectedTimeFirst);
    }
    if (selectedTimeSecond) {
      setLocalSelectedTimeSecond(selectedTimeSecond);
    }
  }, [selectedDayFirst, selectedDaySecond, selectedTimeFirst, selectedTimeSecond]);

  const days = [
    { id: 1, name: "월" },
    { id: 2, name: "화" },
    { id: 3, name: "수" },
    { id: 4, name: "목" },
    { id: 5, name: "금" },
    { id: 6, name: "토" },
    { id: 7, name: "일" },
  ];

  const handleDaySelect = (day: string) => {
    setLocalSelectedDay(day);
    // 부모에게 선택된 요일 전달
    if (onDaySelectFirst) {
      const dayId = days.find((d) => d.name === day)?.id;
      if (dayId) {
        onDaySelectFirst(dayId);
      }
    }
  };

  const handleDaySelectSecond = (day: string) => {
    setLocalSelectedDaySecond(day);
    // 부모에게 선택된 요일 전달
    if (onDaySelectSecond) {
      const dayId = days.find((d) => d.name === day)?.id;
      if (dayId) {
        onDaySelectSecond(dayId);
      }
    }
  };

  const handleTimeSelect = (time: string) => {
    setLocalSelectedTime(time);
    // 부모에게 선택된 시간 전달
    if (onTimeSelectFirst) {
      onTimeSelectFirst(time);
    }
  };

  const handleTimeSelectSecond = (time: string) => {
    setLocalSelectedTimeSecond(time);
    // 부모에게 선택된 시간 전달
    if (onTimeSelectSecond) {
      onTimeSelectSecond(time);
    }
  };

  const handleConfirm = () => {
    setShowDayModal(false);
  };

  const handleConfirmSecond = () => {
    setShowDayModalSecond(false);
  };

  const handleTimeConfirm = () => {
    setShowTimeModal(false);
  };

  const handleTimeConfirmSecond = () => {
    setShowTimeModalSecond(false);
  };

  return (
    <ST2Container>
      {/* Main Content */}
      <div className="main-content">
        <h1 className="page-title">라운드 일자를 선택해주세요.</h1>

        {/* Month Selection */}
        <div className="month-section">
          <div className="section-label">
            <span className="required-asterisk">*</span>
            <span className="label-text"> 희망 월 </span>
            <span className="label-subtext">(최소 8회 이상)</span>
          </div>

          <div className="month-grid">
            <MonthRadioBoxGroup />
          </div>
        </div>

        {/* First Choice Section */}
        <div className="date-choice-section">
          <div className="section-label">
            <span className="required-asterisk">*</span>
            <span className="label-text"> 희망일자 1지망</span>
          </div>

          <div className="week-selection">
            <WeekRadioGroupFirst />
          </div>

          <div className="datetime-selectors">
            <button
              className={`datetime-button ${localSelectedDay !== "- 요일" ? "has-value" : ""}`}
              onClick={() => setShowDayModal(true)}
            >
              <span className="datetime-text">{localSelectedDay}</span>
              <div className="datetime-icon">
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.75437 9.5C3.54312 9.5 3.36458 9.42854 3.21875 9.28563C3.07292 9.14271 3 8.96563 3 8.75438C3 8.54313 3.07146 8.36458 3.21438 8.21875C3.35729 8.07292 3.53438 8 3.74563 8C3.95688 8 4.13542 8.07146 4.28125 8.21438C4.42708 8.35729 4.5 8.53438 4.5 8.74563C4.5 8.95688 4.42854 9.13542 4.28562 9.28125C4.14271 9.42708 3.96563 9.5 3.75437 9.5ZM7.00437 9.5C6.79313 9.5 6.61458 9.42854 6.46875 9.28563C6.32292 9.14271 6.25 8.96563 6.25 8.75438C6.25 8.54313 6.32146 8.36458 6.46438 8.21875C6.60729 8.07292 6.78438 8 6.99563 8C7.20688 8 7.38542 8.07146 7.53125 8.21438C7.67708 8.35729 7.75 8.53438 7.75 8.74563C7.75 8.95688 7.67854 9.13542 7.53562 9.28125C7.39271 9.42708 7.21562 9.5 7.00437 9.5ZM10.2544 9.5C10.0431 9.5 9.86458 9.42854 9.71875 9.28563C9.57292 9.14271 9.5 8.96563 9.5 8.75438C9.5 8.54313 9.57146 8.36458 9.71437 8.21875C9.85729 8.07292 10.0344 8 10.2456 8C10.4569 8 10.6354 8.07146 10.7812 8.21438C10.9271 8.35729 11 8.53438 11 8.74563C11 8.95688 10.9285 9.13542 10.7856 9.28125C10.6427 9.42708 10.4656 9.5 10.2544 9.5ZM1.5 16C1.0875 16 0.734375 15.8507 0.440625 15.5521C0.146875 15.2535 0 14.9028 0 14.5V3.5C0 3.09722 0.146875 2.74653 0.440625 2.44792C0.734375 2.14931 1.0875 2 1.5 2H3V0H4.5V2H9.5V0H11V2H12.5C12.9125 2 13.2656 2.14931 13.5594 2.44792C13.8531 2.74653 14 3.09722 14 3.5V14.5C14 14.9028 13.8531 15.2535 13.5594 15.5521C13.2656 15.8507 12.9125 16 12.5 16H1.5ZM1.5 14.5H12.5V7H1.5V14.5ZM1.5 5.5H12.5V3.5H1.5V5.5Z"
                    fill="#1A1A1A"
                  />
                </svg>
              </div>
            </button>

            <button
              className={`datetime-button ${localSelectedTime !== "- 시" ? "has-value" : ""}`}
              onClick={() => setShowTimeModal(true)}
            >
              <span className="datetime-text">{localSelectedTime}</span>
              <div className="datetime-icon">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 18 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.00016 15.9999C8.02794 15.9999 7.11732 15.8152 6.26829 15.4458C5.41912 15.0763 4.68023 14.5772 4.05162 13.9485C3.42287 13.3198 2.92377 12.581 2.55433 11.7318C2.18489 10.8828 2.00016 9.97214 2.00016 8.99992C2.00016 8.0277 2.18489 7.11707 2.55433 6.26804C2.92377 5.41888 3.42287 4.67999 4.05162 4.05138C4.68023 3.42263 5.41912 2.92353 6.26829 2.55408C7.11732 2.18464 8.02794 1.99992 9.00016 1.99992C9.97239 1.99992 10.883 2.18464 11.732 2.55408C12.5812 2.92353 13.3201 3.42263 13.9487 4.05138C14.5775 4.67999 15.0766 5.41888 15.446 6.26804C15.8154 7.11707 16.0002 8.0277 16.0002 8.99992C16.0002 9.97214 15.8154 10.8828 15.446 11.7318C15.0766 12.581 14.5775 13.3198 13.9487 13.9485C13.3201 14.5772 12.5812 15.0763 11.732 15.4458C10.883 15.8152 9.97239 15.9999 9.00016 15.9999ZM11.0835 11.8333L12.146 10.7708L9.75016 8.37492V4.99992H8.25016V8.99992L11.0835 11.8333ZM3.93766 0.395752L5.00016 1.45825L1.4585 4.99992L0.395996 3.93742L3.93766 0.395752ZM14.0627 0.395752L17.6043 3.93742L16.5418 4.99992L13.0002 1.45825L14.0627 0.395752ZM8.99558 14.4999C10.5264 14.4999 11.8266 13.9667 12.896 12.9003C13.9654 11.8339 14.5002 10.5353 14.5002 9.0045C14.5002 7.47367 13.967 6.17353 12.9006 5.10409C11.8342 4.03464 10.5356 3.49992 9.00475 3.49992C7.47391 3.49992 6.17377 4.03311 5.10433 5.0995C4.03489 6.16589 3.50016 7.4645 3.50016 8.99534C3.50016 10.5262 4.03336 11.8263 5.09975 12.8958C6.16614 13.9652 7.46475 14.4999 8.99558 14.4999Z"
                    fill="#1A1A1A"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Second Choice Section */}
        <div className="date-choice-section">
          <div className="section-label">
            <span className="required-asterisk">*</span>
            <span className="label-text"> 희망일자 2지망</span>
          </div>

          <div className="week-selection">
            <WeekRadioGroupSecond />
          </div>

          <div className="datetime-selectors">
            <button
              className={`datetime-button ${
                localSelectedDaySecond !== "- 요일" ? "has-value" : ""
              }`}
              onClick={() => setShowDayModalSecond(true)}
            >
              <span className="datetime-text">{localSelectedDaySecond}</span>
              <div className="datetime-icon">
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.75437 9.5C3.54312 9.5 3.36458 9.42854 3.21875 9.28563C3.07292 9.14271 3 8.96563 3 8.75438C3 8.54313 3.07146 8.36458 3.21438 8.21875C3.35729 8.07292 3.53438 8 3.74563 8C3.95688 8 4.13542 8.07146 4.28125 8.21438C4.42708 8.35729 4.5 8.53438 4.5 8.74563C4.5 8.95688 4.42854 9.13542 4.28562 9.28125C4.14271 9.42708 3.96563 9.5 3.75437 9.5ZM7.00437 9.5C6.79313 9.5 6.61458 9.42854 6.46875 9.28563C6.32292 9.14271 6.25 8.96563 6.25 8.75438C6.25 8.54313 6.32146 8.36458 6.46438 8.21875C6.60729 8.07292 6.78438 8 6.99563 8C7.20688 8 7.38542 8.07146 7.53125 8.21438C7.67708 8.35729 7.75 8.53438 7.75 8.74563C7.75 8.95688 7.67854 9.13542 7.53562 9.28125C7.39271 9.42708 7.21562 9.5 7.00437 9.5ZM10.2544 9.5C10.0431 9.5 9.86458 9.42854 9.71875 9.28563C9.57292 9.14271 9.5 8.96563 9.5 8.75438C9.5 8.54313 9.57146 8.36458 9.71437 8.21875C9.85729 8.07292 10.0344 8 10.2456 8C10.4569 8 10.6354 8.07146 10.7812 8.21438C10.9271 8.35729 11 8.53438 11 8.74563C11 8.95688 10.9285 9.13542 10.7856 9.28125C10.6427 9.42708 10.4656 9.5 10.2544 9.5ZM1.5 16C1.0875 16 0.734375 15.8507 0.440625 15.5521C0.146875 15.2535 0 14.9028 0 14.5V3.5C0 3.09722 0.146875 2.74653 0.440625 2.44792C0.734375 2.14931 1.0875 2 1.5 2H3V0H4.5V2H9.5V0H11V2H12.5C12.9125 2 13.2656 2.14931 13.5594 2.44792C13.8531 2.74653 14 3.09722 14 3.5V14.5C14 14.9028 13.8531 15.2535 13.5594 15.5521C13.2656 15.8507 12.9125 16 12.5 16H1.5ZM1.5 14.5H12.5V7H1.5V14.5ZM1.5 5.5H12.5V3.5H1.5V5.5Z"
                    fill="#1A1A1A"
                  />
                </svg>
              </div>
            </button>

            <button
              className={`datetime-button ${localSelectedTimeSecond !== "- 시" ? "has-value" : ""}`}
              onClick={() => setShowTimeModalSecond(true)}
            >
              <span className="datetime-text">{localSelectedTimeSecond}</span>
              <div className="datetime-icon">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 18 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.00016 15.9999C8.02794 15.9999 7.11732 15.8152 6.26829 15.4458C5.41912 15.0763 4.68023 14.5772 4.05162 13.9485C3.42287 13.3198 2.92377 12.581 2.55433 11.7318C2.18489 10.8828 2.00016 9.97214 2.00016 8.99992C2.00016 8.0277 2.18489 7.11707 2.55433 6.26804C2.92377 5.41888 3.42287 4.67999 4.05162 4.05138C4.68023 3.42263 5.41912 2.92353 6.26829 2.55408C7.11732 2.18464 8.02794 1.99992 9.00016 1.99992C9.97239 1.99992 10.883 2.18464 11.732 2.55408C12.5812 2.92353 13.3201 3.42263 13.9487 4.05138C14.5775 4.67999 15.0766 5.41888 15.446 6.26804C15.8154 7.11707 16.0002 8.0277 16.0002 8.99992C16.0002 9.97214 15.8154 10.8828 15.446 11.7318C15.0766 12.581 14.5775 13.3198 13.9487 13.9485C13.3201 14.5772 12.5812 15.0763 11.732 15.4458C10.883 15.8152 9.97239 15.9999 9.00016 15.9999ZM11.0835 11.8333L12.146 10.7708L9.75016 8.37492V4.99992H8.25016V8.99992L11.0835 11.8333ZM3.93766 0.395752L5.00016 1.45825L1.4585 4.99992L0.395996 3.93742L3.93766 0.395752ZM14.0627 0.395752L17.6043 3.93742L16.5418 4.99992L13.0002 1.45825L14.0627 0.395752ZM8.99558 14.4999C10.5264 14.4999 11.8266 13.9667 12.896 12.9003C13.9654 11.8339 14.5002 10.5353 14.5002 9.0045C14.5002 7.47367 13.967 6.17353 12.9006 5.10409C11.8342 4.03464 10.5356 3.49992 9.00475 3.49992C7.47391 3.49992 6.17377 4.03311 5.10433 5.0995C4.03489 6.16589 3.50016 7.4645 3.50016 8.99534C3.50016 10.5262 4.03336 11.8263 5.09975 12.8958C6.16614 13.9652 7.46475 14.4999 8.99558 14.4999Z"
                    fill="#1A1A1A"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
        <div className="bottom-nav">
          <div className="button-group">
            <button className="prev-button" onClick={onPrevious}>
              뒤로
            </button>
            <button
              className="next-button"
              onClick={onNext}
              disabled={
                !selectedMonthIds ||
                selectedMonthIds.length === 0 ||
                !selectedWeekIdsFirst ||
                selectedWeekIdsFirst.length === 0 ||
                !selectedWeekIdsSecond ||
                selectedWeekIdsSecond.length === 0 ||
                localSelectedDay === "- 요일" ||
                localSelectedTime === "- 시" ||
                localSelectedDaySecond === "- 요일" ||
                localSelectedTimeSecond === "- 시"
              }
            >
              다음
            </button>
          </div>
        </div>
      </div>

      {/* Day Selection Modal - First Choice */}
      {showDayModal && (
        <div className="modal-overlay" onClick={() => setShowDayModal(false)}>
          <div className="modal-container" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">라운드 요일</h2>
              </div>

              <div className="options-container day-options">
                {days.map((day) => (
                  <button
                    key={day.id}
                    className={`option-button day-option ${
                      localSelectedDay === day.name ? "selected" : ""
                    }`}
                    onClick={() => handleDaySelect(day.name)}
                  >
                    <div className="option-content">
                      <div
                        className={`check-icon ${localSelectedDay === day.name ? "active" : ""}`}
                      >
                        {localSelectedDay === day.name && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="option-text">{day.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-buttons">
              <button className="cancel-button" onClick={() => setShowDayModal(false)}>
                뒤로
              </button>
              <button className="select-button" onClick={handleConfirm}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Selection Modal - Second Choice */}
      {showDayModalSecond && (
        <div className="modal-overlay" onClick={() => setShowDayModalSecond(false)}>
          <div className="modal-container" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">라운드 요일</h2>
              </div>

              <div className="options-container day-options">
                {days.map((day) => (
                  <button
                    key={day.id}
                    className={`option-button day-option ${
                      localSelectedDaySecond === day.name ? "selected" : ""
                    }`}
                    onClick={() => handleDaySelectSecond(day.name)}
                  >
                    <div className="option-content">
                      <div
                        className={`check-icon ${
                          localSelectedDaySecond === day.name ? "active" : ""
                        }`}
                      >
                        {localSelectedDaySecond === day.name && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="option-text">{day.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-buttons">
              <button className="cancel-button" onClick={() => setShowDayModalSecond(false)}>
                뒤로
              </button>
              <button className="select-button" onClick={handleConfirmSecond}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Selection Modal - First Choice */}
      {showTimeModal && (
        <div className="modal-overlay" onClick={() => setShowTimeModal(false)}>
          <div className="modal-container" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">시작 시간</h2>
              </div>

              <div className="options-container">
                {times.map((time) => (
                  <button
                    key={time.id}
                    className={`option-button ${localSelectedTime === time.name ? "selected" : ""}`}
                    onClick={() => handleTimeSelect(time.name)}
                  >
                    <div className="option-content">
                      <div
                        className={`check-icon ${localSelectedTime === time.name ? "active" : ""}`}
                      >
                        {localSelectedTime === time.name && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="option-text">{time.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-buttons">
              <button className="cancel-button" onClick={() => setShowTimeModal(false)}>
                뒤로
              </button>
              <button className="select-button" onClick={handleTimeConfirm}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Selection Modal - Second Choice */}
      {showTimeModalSecond && (
        <div className="modal-overlay" onClick={() => setShowTimeModalSecond(false)}>
          <div className="modal-container" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">시작 시간</h2>
              </div>

              <div className="options-container">
                {times.map((time) => (
                  <button
                    key={time.id}
                    className={`option-button ${
                      localSelectedTimeSecond === time.name ? "selected" : ""
                    }`}
                    onClick={() => handleTimeSelectSecond(time.name)}
                  >
                    <div className="option-content">
                      <div
                        className={`check-icon ${
                          localSelectedTimeSecond === time.name ? "active" : ""
                        }`}
                      >
                        {localSelectedTimeSecond === time.name && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="option-text">{time.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-buttons">
              <button className="cancel-button" onClick={() => setShowTimeModalSecond(false)}>
                뒤로
              </button>
              <button className="select-button" onClick={handleTimeConfirmSecond}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </ST2Container>
  );
}

const ST2Container = styled.div`
  :root {
    --teeshot-white: #fdfdfd;
    --teeshot-black: #1a1a1a;
    --teeshot-gray-2: #d1d5d8;
    --teeshot-gray-3: #f0f0f4;
    --teeshot-gray-1: #79818a;
    --teeshot-active: #007bff;
  }
  padding: 16px;
  width: 100%;
  background: var(--teeshot-white);
  position: relative;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;

  /* Main Content */
  .main-content {
    width: 100%;
    height: 100vh;
  }

  .page-title {
    color: var(--teeshot-black);
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    margin: 0 0 32px 0;
    width: 100%;
    height: 24px;
    margin-top: 8px;
  }

  /* Section Labels */
  .section-label {
    margin-bottom: 12px;
    height: 18px;
  }

  .required-asterisk {
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: #f97000;
  }

  .label-text {
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: var(--teeshot-black);
  }

  .label-subtext {
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: var(--teeshot-gray-1);
  }

  /* Month Selection */
  .month-section {
    margin-bottom: 40px;
  }

  .month-button {
    display: flex;
    height: 44px;
    padding: 13px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    background: var(--teeshot-gray-3);
    border: none;
    cursor: pointer;
    color: var(--teeshot-black);
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    box-sizing: border-box;
  }

  .month-button:hover {
    background: var(--teeshot-gray-2);
  }

  .month-button:active,
  .month-button.selected {
    background: var(--teeshot-active);
    color: var(--teeshot-white);
  }

  /* Date Choice Sections */
  .date-choice-section {
    margin-bottom: 40px;
  }

  .week-selection {
    gap: 9px;
    margin-bottom: 12px;
    height: 44px;
  }

  .week-button {
    display: flex;
    width: 83px;
    height: 44px;
    padding: 13px 25px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    background: var(--teeshot-gray-3);
    border: none;
    cursor: pointer;
    color: var(--teeshot-black);
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    box-sizing: border-box;
  }

  .week-button:hover {
    background: var(--teeshot-gray-2);
  }

  .week-button:active,
  .week-button.selected {
    background: var(--teeshot-active);
    color: var(--teeshot-white);
  }

  .datetime-selectors {
    display: flex;
    gap: 8px;
    height: 44px;
  }

  .datetime-selector {
    flex: 1;
    height: 44px;
  }

  .datetime-button {
    display: flex;
    width: 50%;
    height: 44px;
    padding: 12px 10px;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px;
    background: var(--teeshot-gray-3);
    border: 1px solid transparent;
    cursor: pointer;
    box-sizing: border-box;

    border-radius: 6px;
    background: var(--Teeshot-Neutrals-Color-Teeshot-G3, #f0f0f4);
  }

  .datetime-button.has-value {
    border: 1px solid #007bff;
  }

  .datetime-text {
    color: var(--teeshot-gray-1);
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }

  .datetime-icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .datetime-button:hover {
    background: var(--teeshot-gray-2);
  }

  .datetime-button:active,
  .datetime-button.selected {
    background: var(--teeshot-active);
  }

  .datetime-button:active .datetime-text,
  .datetime-button.selected .datetime-text {
    color: var(--teeshot-white);
  }

  .datetime-button:active .datetime-icon svg,
  .datetime-button.selected .datetime-icon svg {
    fill: var(--teeshot-white);
  }

  /* Day Selection Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(1, 1, 1, 0.86);
    display: flex;
    justify-content: center;
    align-items: flex-end;
    z-index: 9999;
    animation: fade-in 0.2s;
     @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .modal-container {
    max-width: 800px;
    width: calc(100% - 32px);
    height: auto;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    border-radius: 6px;
    background: #fdfdfd;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 0;
  }

  .modal-content {
   width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  flex: 1;
  }

  .modal-header {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .modal-title {
    align-self: stretch;
    color: #1a1a1a;
    font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
  }

  .options-container {
   display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  }

  .option-button {
    display: flex;
    min-width: 120px;
    flex: 1;
    height: 40px;
    justify-content: flex-start;
    align-items: center;
    border-radius: 6px;
    background: #f8f9fa;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .option-button.selected {
    border: 1px solid #007bff;
  }

  .option-button:hover {
    background: #f1f3f4;
  }

  .day-option {
    width: 100%;
    flex: none;
  }

  .option-content {
    display: flex;
    padding: 8px 10px;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .check-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    aspect-ratio: 1/1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: #d1d5d8;
    color: white;
  }

  .check-icon.active {
    background-color: #007bff;
    color: white;
  }

  .option-text {
    color: #1a1a1a;
    font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    margin-left: 10px;
  }

  .modal-buttons {
    display: flex;
    height: 60px;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .cancel-button {
    display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 0 6px;
  background: #9ca3af;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
  }


  .select-button {
    display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 6px 0;
  background: #004b49;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  }

  .select-button:hover {
    opacity: 0.9;
  }

  /* Bottom Navigation */
  .bottom-nav {
    max-width: 800px;
    width: 100%;
    justify-content: center;
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: 26px 16px;
    background: var(--teeshot-white);
    border-top: 1px solid var(--teeshot-gray-2);
    z-index: 1;
    display: flex;
    gap: 8px;
  }

  .button-group {
  padding-bottom: 24px;
    gap: 12px;
    width: 100%;
  }

  .prev-button {
    display: flex;
    width: 100%;
    max-width: 400px;
    min-width: 170px;
    height: 60px;
    padding: 19px 20px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 10px;
    background: white !important;
    color: #1a1a1a !important;
    border: 1px solid #1a1a1a !important;
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .prev-button:hover {
    background-color: #f5f5f5 !important;
  }

  .prev-button:active {
    background-color: #e5e5e5 !important;
  }

  .next-button {
    display: flex;
    width: 100%;
    max-width: 400px;
    min-width: 170px;
    height: 60px;
    padding: 19px 20px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 10px;
    background: var(--Teeshot-Primary-Color-Teeshot-Green, #004b49);
    color: white;
    border: none;
    font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .next-button:hover {
    background-color: #003a38;
  }

  .next-button:active {
    background-color: #002a28;
  }

  .next-button:disabled {
    background-color: #d1d5d8 !important;
    color: #79818a !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
  }

  button.next-button:disabled {
    background-color: #d1d5d8 !important;
    color: #79818a !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
  }

  .next-button.disabled-style {
    background-color: #d1d5d8 !important;
    color: #79818a !important;
    cursor: pointer !important;
    opacity: 0.6 !important;
  }
`;
